import { CSSProperties, ReactNode, memo } from 'react';
import { css } from '@emotion/react';
import { breakpoints } from '@frontend/responsiveness';
import { useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Text, TextSize, Truncated } from '@frontend/design-system';

type commonProps = {
  className?: string;
  size?: TextSize;
  style?: CSSProperties;
  weight?: keyof typeof theme.font.weight;
};

export type ChartSneakPeakDataItem = {
  key: string;
  name: string | ReactNode;
  omitSubtext?: boolean;
  subtext?: string;
  value: number | string;
} | null;

type SneakPeakBarProps = commonProps & {
  data: ChartSneakPeakDataItem[];
};

export const SneakPeakBar = memo(
  ({ className = '', data, size = 'medium', style = {}, weight = 'bold' }: SneakPeakBarProps) => {
    const { isSmallMode } = useIsSmallMode();

    return (
      <div className={className} css={styles.wrapper} style={style}>
        {data.map((item) => {
          if (!item) return null;

          const { key, name, omitSubtext, subtext, value } = item;
          return (
            <div className='sneak-peak-item' css={styles.sneakPeakItem} key={key}>
              {typeof name === 'string' ? (
                <Truncated
                  style={{
                    color: theme.font.colors.light,
                    fontSize: theme.font.size[size],
                    fontWeight: theme.font.weight[weight],
                  }}
                >
                  {name}
                </Truncated>
              ) : (
                name
              )}
              <Text
                size={size}
                style={{
                  fontSize: isSmallMode ? theme.fontSize(24) : theme.fontSize(36),
                }}
                weight={weight}
              >
                {value}
              </Text>
              {subtext && !omitSubtext && (
                <Truncated
                  style={{
                    color: theme.font.colors.light,
                    fontSize: theme.font.size.small,
                  }}
                >
                  {subtext}
                </Truncated>
              )}
            </div>
          );
        })}
      </div>
    );
  }
);

SneakPeakBar.displayName = 'SneakPeakBar';

const styles = {
  wrapper: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(2)};
    justify-content: space-between;
  `,

  sneakPeakItem: css`
    align-items: center;
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    flex: 1;
    max-width: calc(100% / 2 - ${theme.spacing(2)});

    @container (min-width: ${breakpoints.medium.max}px) {
      max-width: calc(100% / 3 - ${theme.spacing(2)});
    }

    p {
      max-width: 100%;
      width: auto;
    }
  `,
};
